import React, {Fragment, useEffect, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {Copy, Key, Loader2, Plus, Trash2} from 'lucide-react';
import clsx from 'clsx';
import {toast} from 'react-toastify';
import AdminService from '@/modules/admin/services/adminService';
import type {ProviderApiKey} from '@/shared/types/api';

function copyToClipboard(text: string) {
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(
            () => toast.success('Copied to clipboard'),
            () => fallbackCopy(text)
        );
    } else {
        fallbackCopy(text);
    }
}

function fallbackCopy(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    toast.success('Copied to clipboard');
}

interface Props {
    orgId: number;
}

const ProviderApiKeys: React.FC<Props> = ({orgId}) => {
    const {data: consoleData, isLoading: providersLoading} = useQuery({
        queryKey: ['admin', 'provider-console', orgId],
        queryFn: () => AdminService.getOrganizationProviderConsole(orgId),
        staleTime: 60_000,
    });
    const providers = (consoleData?.providers ?? []).filter((p) => p.is_active && p.membership_status === 'active');
    const queryClient = useQueryClient();
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);

    useEffect(() => {
        if (selectedProviderId == null && providers.length > 0) {
            setSelectedProviderId(providers[0].id);
        }
    }, [providers, selectedProviderId]);

    const [createOpen, setCreateOpen] = useState(false);
    const [createLabel, setCreateLabel] = useState('');
    const [revokeConfirmId, setRevokeConfirmId] = useState<number | null>(null);
    const [newKeyValue, setNewKeyValue] = useState<string | null>(null);

    const queryKey = ['admin', 'provider-api-keys', selectedProviderId];

    const {data: keys = [], isLoading} = useQuery({
        queryKey,
        queryFn: () => AdminService.listProviderApiKeys(selectedProviderId!, orgId),
        enabled: selectedProviderId != null,
        staleTime: 30_000,
    });

    const createMutation = useMutation({
        mutationFn: (input: {label?: string}) =>
            AdminService.createProviderApiKey(selectedProviderId!, {...input, organizationId: orgId}),
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey});
            setNewKeyValue(data.key);
            setCreateOpen(false);
            setCreateLabel('');
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to create API key');
        },
    });

    const revokeMutation = useMutation({
        mutationFn: (keyId: number) => AdminService.revokeProviderApiKey(selectedProviderId!, keyId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('API key revoked');
            setRevokeConfirmId(null);
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to revoke API key');
        },
    });

    const selectedProvider = providers.find((p) => p.id === selectedProviderId);

    if (providersLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-mld-muted-text" />
            </div>
        );
    }

    if (providers.length === 0) {
        return <div className="py-8 text-center text-sm text-mld-muted-text">No providers in this organization.</div>;
    }

    return (
        <div className="mt-4">
            {/* Provider selector */}
            <div className="mb-4 flex items-center gap-4">
                <label className="text-xs font-medium text-mld-text-secondary">Provider:</label>
                <select
                    value={selectedProviderId ?? ''}
                    onChange={(e) => setSelectedProviderId(Number(e.target.value))}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                    {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>
                <a
                    href="https://api.mylabsdirect.com/api/v1/docs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-mld-primary hover:underline">
                    API Documentation &rarr;
                </a>
            </div>

            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-mld-menu-text">API Keys</h3>
                    <p className="text-xs text-mld-muted-text">
                        Manage API keys for {selectedProvider?.name ?? 'this provider'}. Keys allow external systems to
                        place test kit orders.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-mld-primary-700">
                    <Plus className="h-4 w-4" />
                    Generate Key
                </button>
            </div>

            {/* Keys table */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-mld-muted-text" />
                </div>
            ) : keys.length === 0 ? (
                <div className="rounded-lg border border-dashed border-mld-panel-border py-10 text-center text-sm text-mld-muted-text">
                    <Key className="mx-auto mb-2 h-8 w-8 text-mld-muted-text" />
                    No API keys generated yet.
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-mld-panel-border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-mld-panel-border bg-mld-menu-background text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                <th className="px-4 py-2.5">Key</th>
                                <th className="px-4 py-2.5">Label</th>
                                <th className="px-4 py-2.5">Last Used</th>
                                <th className="px-4 py-2.5">Created</th>
                                <th className="px-4 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k) => (
                                <KeyRow key={k.id} apiKey={k} onRevoke={() => setRevokeConfirmId(k.id)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* New Key Display Modal */}
            <Transition appear show={newKeyValue != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setNewKeyValue(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-lg rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="border-b border-mld-panel-border px-6 py-4">
                                        <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                            API Key Created
                                        </Dialog.Title>
                                        <p className="mt-1 text-xs text-mld-warning font-medium">
                                            Copy this key now. It will not be shown again.
                                        </p>
                                    </div>
                                    <div className="px-6 py-5">
                                        <div className="flex items-center gap-2 rounded-lg border border-mld-panel-border bg-mld-menu-background p-3">
                                            <code className="flex-1 break-all text-xs font-mono text-mld-text-primary">
                                                {newKeyValue}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => copyToClipboard(newKeyValue!)}
                                                className="shrink-0 rounded p-1.5 text-mld-muted-text hover:bg-mld-panel-border hover:text-mld-text-secondary"
                                                title="Copy">
                                                <Copy className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setNewKeyValue(null)}
                                            className="rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700">
                                            Done
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Create Key Modal */}
            <Transition appear show={createOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setCreateOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="border-b border-mld-panel-border px-6 py-4">
                                        <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                            Generate API Key
                                        </Dialog.Title>
                                        <p className="text-xs text-mld-muted-text">For {selectedProvider?.name}</p>
                                    </div>
                                    <div className="px-6 py-5">
                                        <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                            Label <span className="font-normal text-mld-muted-text">(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={createLabel}
                                            onChange={(e) => setCreateLabel(e.target.value)}
                                            placeholder="e.g. EHR Integration"
                                            className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCreateOpen(false);
                                                setCreateLabel('');
                                            }}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                createMutation.mutate({label: createLabel.trim() || undefined})
                                            }
                                            disabled={createMutation.isPending}
                                            className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                            {createMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            Generate
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Revoke Confirmation Modal */}
            <Transition appear show={revokeConfirmId != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setRevokeConfirmId(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-sm rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="px-6 py-5">
                                        <Dialog.Title className="text-base font-semibold text-mld-menu-text">
                                            Revoke API Key
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-mld-muted-text">
                                            This key will immediately stop working. Any integration using it will lose
                                            access. This cannot be undone.
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setRevokeConfirmId(null)}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                revokeConfirmId != null && revokeMutation.mutate(revokeConfirmId)
                                            }
                                            disabled={revokeMutation.isPending}
                                            className={clsx(
                                                'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-60',
                                                'bg-mld-danger/50 hover:bg-mld-danger'
                                            )}>
                                            {revokeMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            Revoke
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

// ── Key Row ─────────────────────────────────────────────────────────

function KeyRow({apiKey, onRevoke}: {apiKey: ProviderApiKey; onRevoke: () => void}) {
    const formatDate = (iso: string | null) => {
        if (!iso) return <span className="text-mld-muted-text">&mdash;</span>;
        return new Date(iso).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
    };

    return (
        <tr className="border-b border-mld-panel-border/30 last:border-0">
            <td className="px-4 py-2.5 font-mono text-xs text-mld-text-secondary">{apiKey.key_prefix}...</td>
            <td className="px-4 py-2.5 text-mld-text-secondary">
                {apiKey.label || <span className="text-mld-muted-text">&mdash;</span>}
            </td>
            <td className="px-4 py-2.5 text-xs text-mld-muted-text">{formatDate(apiKey.last_used_at)}</td>
            <td className="px-4 py-2.5 text-xs text-mld-muted-text">{formatDate(apiKey.created_at)}</td>
            <td className="px-4 py-2.5 text-right">
                <button
                    type="button"
                    onClick={onRevoke}
                    className="rounded p-1 text-mld-muted-text hover:bg-mld-danger/5 hover:text-mld-danger"
                    title="Revoke">
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </td>
        </tr>
    );
}

export default ProviderApiKeys;
